// PropertyCheckList.tsx
import React from 'react';
import './PropertyCheckList.scss';

export type Property = {
  id: string;
  name: string;
  location: {
    city?: string;
    countryCode?: string;
    formattedLocation?: string;
    [key: string]: any;
  };
  ratePlans?: Array<{
    id: string;
    name: string;
    description: string;
  }>;
  [key: string]: any;
};

interface PropertyCheckListProps {
  data?: {
    properties?: Property[];
    [key: string]: any;
  };
  value: Record<string, string[]>; // Changed to object with propertyId as key and array of rate plan IDs as value
  onChange: (next: Record<string, string[]>) => void;
  showRatePlans?: boolean;
  isLoadingRatePlans?: boolean;
  onPropertyExpand?: (propertyId: string) => void;
}

const PropertyCheckList: React.FC<PropertyCheckListProps> = ({
  data,
  value = {},
  onChange,
  showRatePlans = false,
  isLoadingRatePlans = false,
  onPropertyExpand
}) => {
  const [expandedProperties, setExpandedProperties] = React.useState<Record<string, boolean>>({});
  const [propertyErrors, setPropertyErrors] = React.useState<Record<string, string>>({});

  const isPropertyChecked = (propertyId: string) => propertyId in value;
  const isPropertyExpanded = (propertyId: string) => expandedProperties[propertyId] || false;
  const isRatePlanChecked = (propertyId: string, ratePlanId: string) =>
    value[propertyId]?.includes(ratePlanId) || false;

  // Check if property has rate plans available
  const hasRatePlans = (property: Property) => {
    return property.ratePlans && property.ratePlans.length > 0;
  };

  // Check if property is disabled (no rate plans loaded yet, or no rate plans after loading)
  const isPropertyDisabled = (property: Property) => {
    // Disable if rate plans are not loaded yet (undefined means not fetched)
    if (property.ratePlans === undefined) {
      return true;
    }
    // Disable if rate plans are loaded but empty
    return property.ratePlans.length === 0;
  };

  const togglePropertyExpansion = (propertyId: string) => {
    const isCurrentlyExpanded = expandedProperties[propertyId];

    setExpandedProperties(prev => ({
      ...prev,
      [propertyId]: !prev[propertyId]
    }));

    // Trigger API call when expanding (not collapsing)
    if (!isCurrentlyExpanded && onPropertyExpand) {
      onPropertyExpand(propertyId);
    }
  };

  const togglePropertyCheckbox = (propertyId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click event

    // Find the property
    const property = (data?.properties || []).find(p => p.id === propertyId);

    // Don't allow checking if property rate plans are not loaded or empty
    if (property && isPropertyDisabled(property)) {
      if (property.ratePlans === undefined) {
        setPropertyErrors(prev => ({
          ...prev,
          [propertyId]: 'Please expand this property to load rate plans before selecting'
        }));
      } else {
        setPropertyErrors(prev => ({
          ...prev,
          [propertyId]: 'This property has no rate plans available'
        }));
      }
      return;
    }

    const next = { ...value };
    if (propertyId in next) {
      delete next[propertyId];
      // Clear error when unchecking
      setPropertyErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[propertyId];
        return newErrors;
      });
    } else {
      // When selecting a property, preselect all its rate plans
      const allRatePlanIds = property?.ratePlans?.map(rp => rp.id) || [];
      next[propertyId] = allRatePlanIds;
    }
    onChange(next);
  };

  const toggleRatePlan = (propertyId: string, ratePlanId: string) => {
    const next = { ...value };
    const ratePlans = [...(next[propertyId] || [])];
    const index = ratePlans.indexOf(ratePlanId);

    if (index > -1) {
      ratePlans.splice(index, 1);
    } else {
      ratePlans.push(ratePlanId);
    }

    next[propertyId] = ratePlans;
    onChange(next);
  };

  const properties = data?.properties || [];

  if (properties.length === 0) {
    return <div className="property-check-list">No properties available</div>;
  }

  return (
    <div className="property-check-list">
      <div className="property-check-list__scroll">
        {properties.map(property => {
          const isDisabled = isPropertyDisabled(property);
          const hasError = propertyErrors[property.id];
          const needsRatePlan = isPropertyChecked(property.id) && (!value[property.id] || value[property.id].length === 0);

          return (
            <div key={property.id} className="property-item-container">
              <div
                className={`property-item${isPropertyExpanded(property.id) ? ' expanded' : ''}`}
                onClick={() => togglePropertyExpansion(property.id)}
              >
                <div className="property-item__text">
                  <div className="property-item__title">
                    {property.name}
                    {property.ratePlans !== undefined && property.ratePlans.length === 0 && (
                      <span className="property-item__disabled-badge">No rate plans</span>
                    )}
                  </div>
                  <div className="property-item__subtitle">
                    {property.location?.formattedLocation ||
                     [property.location?.city, property.location?.countryCode]
                      .filter(Boolean)
                      .join(', ')}
                  </div>
                </div>
                <div className="property-item__actions">
                  {showRatePlans && (
                    <span className={`property-item__expand-icon${isPropertyExpanded(property.id) ? ' expanded' : ''}`}>
                      ▼
                    </span>
                  )}
                  <button
                    type="button"
                    className={`property-item__checkbox${isPropertyChecked(property.id) ? ' checked' : ''}${isDisabled ? ' disabled' : ''}`}
                    onClick={(e) => togglePropertyCheckbox(property.id, e)}
                    aria-label={`Select ${property.name}`}
                    disabled={isDisabled}
                  >
                    {isPropertyChecked(property.id) && (
                      <span className="property-item__tick" />
                    )}
                  </button>
                </div>
              </div>

              {hasError && (
                <div className="property-item__error">
                  {hasError}
                </div>
              )}

              {needsRatePlan && (
                <div className="property-item__warning">
                  Please select at least one rate plan for this property
                </div>
              )}

              {showRatePlans && isPropertyExpanded(property.id) && (
                <div className="rate-plans">
                  {!property.ratePlans ? (
                    <div className="rate-plans-loading">
                      Loading rate plans...
                    </div>
                  ) : property.ratePlans.length === 0 ? (
                    <div className="rate-plans-empty">
                      No rate plans available for this property
                    </div>
                  ) : (
                    property.ratePlans.map(ratePlan => (
                      <div key={ratePlan.id} className="rate-plan-item">
                        <label className="rate-plan-checkbox">
                          <input
                            type="checkbox"
                            checked={isRatePlanChecked(property.id, ratePlan.id)}
                            onChange={() => toggleRatePlan(property.id, ratePlan.id)}
                          />
                          <span className="checkmark"></span>
                          <div className="rate-plan-details">
                            <div className="rate-plan-name">{ratePlan.name}</div>
                            {ratePlan.description && (
                              <div className="rate-plan-description">{ratePlan.description}</div>
                            )}
                          </div>
                        </label>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PropertyCheckList;